import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { apiFetch } from "@/api/client";
import { getClient, listListAnalyses, listMeetingsQS } from "@/api/lookups";
import type { MeetingSummary, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/table/DataTable";
import {
  makeMeetingSelectColumn,
  useMeetingSelection,
} from "@/components/table/meetingSelection";
import { ClientFormDialog } from "@/components/registries/ClientFormDialog";
import { ListAnalysisDialog } from "@/components/analysis/ListAnalysisDialog";
import { ListAnalysisHistory } from "@/components/analysis/ListAnalysisHistory";
import { useCan } from "@/hooks/useCan";
import { useFolders } from "@/hooks/useFolders";
import { useMeetingColumns } from "@/pages/registries/useMeetingColumns";

export interface ClientCardViewProps {
  clientId: UUID;
}

/** Hard cap on the card's meetings list — no paginator in v1 (TM-035ae774). */
const MEETINGS_LIMIT = 50;

/**
 * Client card (TM-035ae774).
 *
 * Symmetric with `EmployeeCardView` minus the deactivate toggle (the Client
 * model has no `is_active` soft-delete). Header shows the name + «Изменить»
 * (the existing `ClientFormDialog`); the «Встречи (N)» section renders the
 * shared meetings table filtered by `client_id` (the client column is hidden
 * since every row belongs to this client).
 */
export function ClientCardView({ clientId }: ClientCardViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canEdit = useCan("client_crud");
  const canListAnalyze = useCan("list_analysis_create");
  const [editing, setEditing] = useState(false);
  const selection = useMeetingSelection();
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const { folders } = useFolders();

  const client = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => getClient(clientId),
  });

  const meetings = useQuery({
    queryKey: ["meetings", "by-client", clientId],
    queryFn: () =>
      apiFetch<MeetingSummary[]>(
        `/api/meetings${listMeetingsQS({
          client_id: clientId,
          limit: MEETINGS_LIMIT,
        })}`,
        { method: "GET" },
      ),
  });

  // Feature-probe for list-analysis (TM-4c1fb82b): 404 → hide the UI.
  const listProbeQ = useQuery({
    queryKey: ["list-analyses", "probe", { clientId }],
    queryFn: () => listListAnalyses({ client_id: clientId, limit: 1 }),
    enabled: canListAnalyze,
    retry: false,
    staleTime: 60_000,
  });
  const listFeatureEnabled =
    canListAnalyze && listProbeQ.isSuccess && Array.isArray(listProbeQ.data);

  const baseColumns = useMeetingColumns({ hideClient: true });
  const columns = useMemo<ColumnDef<MeetingSummary, unknown>[]>(() => {
    if (!listFeatureEnabled) return baseColumns;
    return [makeMeetingSelectColumn(selection), ...baseColumns];
  }, [baseColumns, listFeatureEnabled, selection]);

  if (client.isLoading) {
    return <p>{t("clients.card.loading")}</p>;
  }
  if (client.isError || client.data === undefined) {
    return (
      <p role="alert" className="text-destructive">
        {t("clients.card.loadError")}
      </p>
    );
  }

  const cli = client.data;
  const folderNameById = new Map(folders.map((f) => [f.id, f.name]));
  const folderName =
    cli.folder_id !== null && cli.folder_id !== undefined
      ? (folderNameById.get(cli.folder_id) ?? cli.folder_id)
      : null;
  const meetingRows = meetings.data ?? [];

  return (
    <div className="space-y-4" data-testid="client-card">
      <Link
        to="/clients"
        className="text-sm text-primary underline-offset-4 hover:underline"
        data-testid="client-card-back"
      >
        {t("clients.card.backToList")}
      </Link>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle data-testid="client-card-title">{cli.name}</CardTitle>
          <div className="flex items-center gap-2">
            {canEdit ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                data-testid="client-card-edit"
              >
                {t("clients.card.edit")}
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            {/* ИНН intentionally hidden from the card (TM-f518b4c8). The
                value is still stored/edited via the form dialog. */}
            <Field
              label={t("clients.fields.description")}
              value={cli.description}
            />
            <Field
              label={t("clients.fields.aliases")}
              value={cli.aliases.length > 0 ? cli.aliases.join(", ") : null}
            />
            <Field label={t("folders.picker.label")} value={folderName} />
            {Object.entries(cli.contacts).map(([k, v]) => (
              <Field
                key={`contact-${k}`}
                label={k}
                value={
                  v === null || v === undefined || v === "" ? null : String(v)
                }
              />
            ))}
            {Object.entries(cli.custom_field_values).map(([k, v]) => (
              <Field
                key={k}
                label={k}
                value={
                  v === null || v === undefined || v === "" ? null : String(v)
                }
              />
            ))}
          </dl>
        </CardContent>
      </Card>

      <section className="space-y-2" data-testid="client-card-meetings">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {t("clients.card.meetings", { count: meetingRows.length })}
          </h2>
          {listFeatureEnabled ? (
            <Button
              type="button"
              size="sm"
              disabled={selection.count === 0}
              onClick={() => setListDialogOpen(true)}
              data-testid="client-card-list-analyze-button"
            >
              {selection.count > 0
                ? t("listAnalysis.section.analyzeSelected", {
                    count: selection.count,
                  })
                : t("listAnalysis.section.analyzeZero")}
            </Button>
          ) : null}
        </div>
        <DataTable
          data={meetingRows}
          columns={columns}
          testId="client-card-meetings-table"
          isLoading={meetings.isLoading}
          emptyMessage={t("clients.card.noMeetings")}
          onRowClick={(row) =>
            void navigate({
              to: "/meetings/$meetingId",
              params: { meetingId: row.id },
            })
          }
        />
      </section>

      {listFeatureEnabled ? (
        <ListAnalysisHistory mode="client" clientId={clientId} />
      ) : null}

      {listFeatureEnabled ? (
        <ListAnalysisDialog
          open={listDialogOpen}
          mode="client"
          clientId={clientId}
          selectedMeetingIds={Array.from(selection.selectedIds)}
          onClose={() => setListDialogOpen(false)}
          onSubmitted={() => selection.clear()}
        />
      ) : null}

      <ClientFormDialog
        open={editing}
        existing={cli}
        onClose={() => setEditing(false)}
      />
    </div>
  );
}

/** One label/value pair in the card's definition list. */
function Field({ label, value }: { label: string; value: string | null }) {
  const { t } = useTranslation();
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5">{value ?? t("common.dash")}</dd>
    </div>
  );
}
